import { useRef, useCallback, useEffect } from "react";

/**
 * useBargeIn — Voice Activity Detection for barge-in interruption
 *
 * Uses getUserMedia with echoCancellation to get a mic stream that filters
 * out TTS audio from speakers. Monitors RMS energy levels:
 *   - 450ms startup grace period (let echo cancellation converge)
 *   - 300ms sustained speech → duck (lower TTS volume, threshold 0.07)
 *   - 700ms sustained speech → full barge-in (stop TTS, start listening, threshold 0.112)
 *
 * Includes 120ms silence grace period so natural micro-pauses in speech
 * don't reset the detection timers.
 *
 * Uses setTimeout loop (not rAF) so detection continues in background tabs.
 *
 * Zero external dependencies — just Web Audio API + getUserMedia.
 */

interface UseBargeInOptions {
  /** Whether barge-in monitoring is active (only during speaking phase) */
  enabled: boolean;
  /** RMS threshold above which we consider the user speaking (0-1) */
  threshold?: number;
  /** ms of sustained speech before ducking TTS */
  duckAfterMs?: number;
  /** ms of sustained speech before full barge-in */
  bargeInAfterMs?: number;
  /** Called when user starts speaking over TTS — duck volume */
  onDuck: () => void;
  /** Called when user stops speaking before barge-in — restore volume */
  onUnduck: () => void;
  /** Called when user speech sustained long enough — full interruption */
  onBargeIn: () => void;
}

const MONITOR_INTERVAL_MS = 50;
const SILENCE_GRACE_MS = 120;
/** RMS threshold for the destructive full barge-in action — must be higher than duck */
const BARGE_IN_THRESHOLD_MULTIPLIER = 1.6;

export function useBargeIn(options: UseBargeInOptions) {
  const {
    enabled,
    threshold = 0.07,
    duckAfterMs = 300,
    bargeInAfterMs = 700,
    onDuck,
    onUnduck,
    onBargeIn,
  } = options;

  // Stable refs for callbacks — avoids restarting the effect when callbacks change identity
  const onDuckRef = useRef(onDuck);
  onDuckRef.current = onDuck;
  const onUnduckRef = useRef(onUnduck);
  onUnduckRef.current = onUnduck;
  const onBargeInRef = useRef(onBargeIn);
  onBargeInRef.current = onBargeIn;

  // Config refs — read inside monitor loop, avoids restarting on value changes
  const thresholdRef = useRef(threshold);
  thresholdRef.current = threshold;
  const duckAfterMsRef = useRef(duckAfterMs);
  duckAfterMsRef.current = duckAfterMs;
  const bargeInAfterMsRef = useRef(bargeInAfterMs);
  bargeInAfterMsRef.current = bargeInAfterMs;
  // Barge-in uses a higher threshold than duck to prevent false full interrupts
  const bargeInThresholdRef = useRef(threshold * BARGE_IN_THRESHOLD_MULTIPLIER);
  bargeInThresholdRef.current = threshold * BARGE_IN_THRESHOLD_MULTIPLIER;

  // Audio infrastructure
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dataRef = useRef(new Float32Array(256));

  // Monotonic start ID to detect stale getUserMedia resolutions
  const startIdRef = useRef(0);

  // Timing state
  const speechStartRef = useRef<number | null>(null);
  const silenceStartRef = useRef<number | null>(null);
  const isDuckedRef = useRef(false);
  const hasBargedInRef = useRef(false);

  // Grace period: ignore mic input for the first 450ms after TTS starts
  // so echo cancellation can converge on the new audio source
  const STARTUP_GRACE_MS = 450;
  const EARLY_WINDOW_MS = 1200;
  const EARLY_THRESHOLD_MULTIPLIER = 1.35;
  const monitorStartTimeRef = useRef<number>(0);

  const cleanup = useCallback(() => {
    // Invalidate any pending getUserMedia resolution
    startIdRef.current += 1;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    sourceRef.current?.disconnect();
    sourceRef.current = null;
    analyserRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    speechStartRef.current = null;
    silenceStartRef.current = null;
    if (isDuckedRef.current) {
      isDuckedRef.current = false;
      onUnduckRef.current();
    }
    hasBargedInRef.current = false;
  }, []);

  const start = useCallback(async () => {
    cleanup();
    const myStartId = ++startIdRef.current;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Check if we were cancelled while awaiting getUserMedia
      if (startIdRef.current !== myStartId) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;

      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      // Re-check after second await
      if (startIdRef.current !== myStartId) {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        return;
      }

      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3;

      // Connect source → analyser (NOT to destination — we don't want to hear the mic)
      source.connect(analyser);

      sourceRef.current = source;
      analyserRef.current = analyser;
      speechStartRef.current = null;
      silenceStartRef.current = null;
      isDuckedRef.current = false;
      hasBargedInRef.current = false;
      monitorStartTimeRef.current = performance.now();

      const monitor = () => {
        if (!analyserRef.current || startIdRef.current !== myStartId) return;

        // Grace period: skip monitoring for the first 450ms to let echo cancellation converge
        if (performance.now() - monitorStartTimeRef.current < STARTUP_GRACE_MS) {
          timerRef.current = setTimeout(monitor, MONITOR_INTERVAL_MS);
          return;
        }

        // Wrap analyser access in try-catch — AudioContext can die from OS audio
        // session interruption, tab backgrounding, or garbage collection. Without
        // this guard the monitor loop dies silently and barge-in stops working.
        let rms: number;
        try {
          analyserRef.current.getFloatTimeDomainData(dataRef.current);
          let sum = 0;
          for (let i = 0; i < dataRef.current.length; i++) {
            const x = dataRef.current[i];
            sum += x * x;
          }
          rms = Math.sqrt(sum / dataRef.current.length);
        } catch (err) {
          console.warn("[BargeIn] Analyser read failed — stopping monitor:", err);
          return; // Stop loop; cleanup() will handle the rest
        }

        const now = performance.now();

        const elapsed = performance.now() - monitorStartTimeRef.current;
        const isEarlyWindow = elapsed < EARLY_WINDOW_MS + STARTUP_GRACE_MS;
        const effectiveThreshold = isEarlyWindow
          ? thresholdRef.current * EARLY_THRESHOLD_MULTIPLIER
          : thresholdRef.current;
        const effectiveBargeInThreshold = isEarlyWindow
          ? bargeInThresholdRef.current * EARLY_THRESHOLD_MULTIPLIER
          : bargeInThresholdRef.current;

        if (rms > effectiveThreshold) {
          // User is speaking — reset silence grace
          silenceStartRef.current = null;

          if (speechStartRef.current === null) {
            speechStartRef.current = now;
          }

          const duration = now - speechStartRef.current;

          // Check for full barge-in
          if (duration >= bargeInAfterMsRef.current && !hasBargedInRef.current && rms > effectiveBargeInThreshold) {
            hasBargedInRef.current = true;
            isDuckedRef.current = false;
            onBargeInRef.current();
            return; // Stop monitoring — useVoiceMode will handle cleanup
          }

          // Check for duck
          if (duration >= duckAfterMsRef.current && !isDuckedRef.current) {
            isDuckedRef.current = true;
            onDuckRef.current();
          }
        } else {
          // Below threshold — start silence grace period
          if (speechStartRef.current !== null) {
            if (silenceStartRef.current === null) {
              silenceStartRef.current = now;
            }

            // Only reset speech after sustained silence (grace period)
            if (now - silenceStartRef.current >= SILENCE_GRACE_MS) {
              speechStartRef.current = null;
              silenceStartRef.current = null;
              if (isDuckedRef.current && !hasBargedInRef.current) {
                isDuckedRef.current = false;
                onUnduckRef.current();
              }
            }
          }
        }

        // setTimeout loop instead of rAF — continues in background tabs
        timerRef.current = setTimeout(monitor, MONITOR_INTERVAL_MS);
      };

      timerRef.current = setTimeout(monitor, MONITOR_INTERVAL_MS);
    } catch (err) {
      // getUserMedia failed — disable barge-in gracefully.
      // Voice mode still works (TTS plays, STT listens), just without
      // interruption detection. Common causes: permission denied, no mic,
      // iOS WebView restrictions.
      console.warn("[BargeIn] getUserMedia failed — barge-in disabled:", err);
      // Clean up any partial state from the attempt
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      sourceRef.current?.disconnect();
      sourceRef.current = null;
      analyserRef.current = null;
    }
  }, [cleanup]);

  // Start/stop based on enabled flag
  useEffect(() => {
    if (enabled) {
      start();
    } else {
      cleanup();
    }
    return cleanup;
  }, [enabled, start, cleanup]);

  // Close AudioContext on unmount to avoid leaking browser resources
  useEffect(() => {
    return () => {
      try { audioCtxRef.current?.close(); } catch (_) { /* already closed */ }
      audioCtxRef.current = null;
    };
  }, []);

  return { cleanup };
}
